import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Sparkles,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useParseDocument,
  DocumentParseResult,
  DocumentProcessLog,
  ScannedDocumentStatus,
} from "@/hooks/useDocuments";

const MAX_RESCANS = 5;

interface OcrProcessingCardProps {
  documentId: string;
  status: ScannedDocumentStatus;
  parseResult: DocumentParseResult;
  processLogs: DocumentProcessLog[] | undefined;
}

// Helper to format time in Pacific timezone with 12-hour format
function formatPacificTime(dateString: string) {
  return new Date(dateString).toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

// Helper to determine OCR model from parser version
function getOcrModelInfo(parserVersion: string | null | undefined) {
  if (!parserVersion) return { model: "Unknown", isGemini: false };
  if (parserVersion.includes("gemini")) {
    if (parserVersion.includes("3-flash") || parserVersion.includes("gemini-3")) {
      return { model: "Gemini 3 Flash", isGemini: true };
    }
    if (parserVersion.includes("2.5")) {
      return { model: "Gemini 2.5 Flash", isGemini: true };
    }
    return { model: "Gemini Flash", isGemini: true };
  }
  if (parserVersion.includes("haiku")) {
    return { model: "Claude 3.5 Haiku", isGemini: false };
  }
  return { model: "Legacy OCR", isGemini: false };
}

export function OcrProcessingCard({
  documentId,
  status,
  parseResult,
  processLogs,
}: OcrProcessingCardProps) {
  const parseDocument = useParseDocument();
  const [showAiLog, setShowAiLog] = useState(false);
  const [rescanCount, setRescanCount] = useState(0);
  const [rescanCooldown, setRescanCooldown] = useState(false);

  const handleRescan = () => {
    if (rescanCount >= MAX_RESCANS || rescanCooldown) return;

    setRescanCooldown(true);

    parseDocument.mutate(documentId, {
      onSuccess: () => {
        setRescanCount((prev) => prev + 1);
        setTimeout(() => setRescanCooldown(false), 5000);
      },
      onError: () => {
        setTimeout(() => setRescanCooldown(false), 10000);
      },
    });
  };

  const modelInfo = getOcrModelInfo(parseResult.parser_version);

  return (
    <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border border-purple-200 dark:border-purple-800 rounded-lg space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-purple-500" />
        <span className="text-sm font-medium">AI Document Processing</span>
      </div>

      {/* OCR Model Used */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">OCR Model</span>
        <div className="flex items-center gap-1.5">
          {modelInfo.isGemini ? (
            <Zap className="h-3.5 w-3.5 text-blue-500" />
          ) : (
            <Sparkles className="h-3.5 w-3.5 text-orange-500" />
          )}
          <Badge
            variant="outline"
            className={cn(
              "text-xs",
              modelInfo.isGemini
                ? "border-blue-300 text-blue-700 dark:text-blue-300"
                : "border-orange-300 text-orange-700 dark:text-orange-300"
            )}
          >
            {modelInfo.model}
          </Badge>
        </div>
      </div>

      {/* Parse Confidence */}
      {parseResult.confidence_score != null && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Parse Confidence</span>
          <Badge
            className={cn(
              parseResult.confidence_score >= 0.75
                ? "bg-green-500"
                : parseResult.confidence_score >= 0.5
                ? "bg-amber-500"
                : "bg-red-500"
            )}
          >
            {Math.round((parseResult.confidence_score || 0) * 100)}%
          </Badge>
        </div>
      )}

      {/* AI Processing Log - Collapsible */}
      {processLogs && processLogs.length > 0 && (
        <Collapsible open={showAiLog} onOpenChange={setShowAiLog}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-between h-7 px-2 text-xs hover:bg-purple-100 dark:hover:bg-purple-900/30"
            >
              <span className="flex items-center gap-1.5">
                <FileText className="h-3 w-3" />
                Processing Log ({processLogs.length} events)
              </span>
              {showAiLog ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
              {processLogs.map((log) => (
                <div
                  key={log.id}
                  className={cn(
                    "p-2 rounded text-xs border",
                    log.event === "gemini_failed"
                      ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
                      : log.event === "ocr_completed" && log.details.extraction_mode === "pdf_fallback"
                      ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"
                      : "bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700"
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium capitalize">
                      {log.event.replace(/_/g, " ")}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatPacificTime(log.occurred_at)}
                    </span>
                  </div>

                  {/* Show extraction mode info */}
                  {log.event === "ocr_completed" && (
                    <div className="space-y-1 text-[10px]">
                      {log.details.extraction_mode === "pdf_fallback" ? (
                        <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <AlertTriangle className="h-3 w-3" />
                          <span>Gemini OCR insufficient, used Claude fallback</span>
                        </div>
                      ) : log.details.extraction_mode === "text_only" ? (
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle2 className="h-3 w-3" />
                          <span>Gemini OCR succeeded</span>
                        </div>
                      ) : null}

                      {log.details.text_length && (
                        <div className="text-muted-foreground">
                          Extracted {log.details.text_length.toLocaleString()} characters
                        </div>
                      )}

                      {/* Token usage */}
                      {(log.details.gemini_usage || log.details.claude_usage) && (
                        <div className="text-muted-foreground flex gap-3">
                          {log.details.gemini_usage && (
                            <span>Gemini: {log.details.gemini_usage.totalTokens} tokens</span>
                          )}
                          {log.details.claude_usage && (
                            <span>
                              Claude:{" "}
                              {(log.details.claude_usage.input_tokens || 0) +
                                (log.details.claude_usage.output_tokens || 0)}{" "}
                              tokens
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show OCR start info */}
                  {log.event === "ocr_started" && log.details.ocr_model && (
                    <div className="text-[10px] text-muted-foreground">
                      OCR: {log.details.ocr_model} → Extract: {log.details.extraction_model}
                    </div>
                  )}

                  {/* Show Gemini failure details */}
                  {log.event === "gemini_failed" && (
                    <div className="space-y-1 text-[10px]">
                      <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                        <XCircle className="h-3 w-3" />
                        <span>Gemini API Error</span>
                      </div>
                      {log.details.error && (
                        <div className="text-red-500 dark:text-red-400 font-mono break-all">
                          {log.details.error}
                        </div>
                      )}
                      <div className="text-muted-foreground">
                        Model: {log.details.model || "gemini-2.5-flash"}
                      </div>
                    </div>
                  )}

                  {/* Show parsing result */}
                  {(log.event === "parsed" || log.event === "needs_review") && log.details.extracted && (
                    <div className="text-[10px] text-muted-foreground space-y-0.5">
                      {log.details.confidence_score && (
                        <div>Confidence: {Math.round(log.details.confidence_score * 100)}%</div>
                      )}
                      {log.details.extracted.client && <div>Client: {log.details.extracted.client}</div>}
                      {log.details.extracted.date && <div>Date: {log.details.extracted.date}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Rescan button - available for all non-routed documents */}
      {status !== "routed" && (
        <div className="pt-2 border-t border-purple-200 dark:border-purple-700">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs border-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30"
              onClick={handleRescan}
              disabled={rescanCount >= MAX_RESCANS || rescanCooldown || parseDocument.isPending}
            >
              {parseDocument.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  Rescanning...
                </>
              ) : (
                <>
                  <RefreshCw className="h-3 w-3 mr-1.5 text-purple-500" />
                  Rescan Document
                </>
              )}
            </Button>
            <Badge
              variant="outline"
              className={cn(
                "text-xs tabular-nums",
                rescanCount >= MAX_RESCANS
                  ? "border-red-300 text-red-600"
                  : "border-purple-300 text-purple-600"
              )}
            >
              {MAX_RESCANS - rescanCount}/{MAX_RESCANS}
            </Badge>
          </div>
          {rescanCount < MAX_RESCANS && !parseDocument.isPending && (
            <p className="text-[10px] text-muted-foreground text-center mt-1">
              Re-run AI OCR extraction
            </p>
          )}
          {rescanCount >= MAX_RESCANS && (
            <p className="text-[10px] text-red-500 text-center mt-1">
              Rescan limit reached for this session
            </p>
          )}
        </div>
      )}
    </div>
  );
}
